import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { getFollowingPostsAction, clearFollowingPostsAction } from "../redux/actions/postActions";
import Post from "../components/post/Post";
import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import LoadMoreButton from "../components/shared/LoadMoreButton";
import { FaUsers } from "react-icons/fa";

const Following = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const followingPosts = useSelector((state) => state.posts?.followingPosts);
  const totalFollowingPosts = useSelector((state) => state.posts?.totalFollowingPosts);

  const LIMIT = 10;

  useEffect(() => {
    const fetchFollowingPosts = async () => {
      setLoading(true);
      await dispatch(getFollowingPostsAction(LIMIT, 0));
      setLoading(false);
    };

    fetchFollowingPosts();

    return () => {
      dispatch(clearFollowingPostsAction());
    };
  }, [dispatch, LIMIT]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = followingPosts?.length || 0;
    dispatch(getFollowingPostsAction(LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, followingPosts?.length]);

  // Current displayed posts (for UI)
  const displayedPosts = useMemo(() => {
    return (followingPosts || []).map((post) => (
      <Post key={post._id} post={post} />
    ));
  }, [followingPosts]);

  // Determine if we should show load more button
  const shouldShowLoadMore = useMemo(() => {
    if (!followingPosts || followingPosts.length === 0) return false; // No posts to show
    
    // Use total count for comparison
    const currentCount = followingPosts?.length || 0;
    return currentCount < totalFollowingPosts;
  }, [followingPosts, totalFollowingPosts]);

  // Handle scrolling to a specific post when navigating back from post detail
  useEffect(() => {
    if (location.state?.scrollToPost && !loading && followingPosts && followingPosts.length > 0) {
      const postId = location.state.scrollToPost;
      // Use setTimeout to ensure DOM is fully rendered
      const scrollTimer = setTimeout(() => {
        const postElement = document.getElementById(`post-${postId}`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the state to prevent re-scrolling on re-renders
          window.history.replaceState({ ...location.state, scrollToPost: undefined }, '');
        }
      }, 200);
      return () => clearTimeout(scrollTimer);
    }
  }, [location.state, loading, followingPosts]);

  return (
    <ContentWithCards>
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <CommonLoading />
        </div>
      ) : followingPosts && followingPosts.length > 0 ? (
        <>
          <div className="w-full space-y-3 flex flex-col max-w-3xl mx-auto overflow-visible">
            {displayedPosts}
          </div>
          
          {/* Load More Button */}
          {shouldShowLoadMore && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadMoreLoading}
              text="Load More Posts"
            />
          )}
        </>
      ) : (
        <div className="w-full bg-white border rounded-lg p-8 text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FaUsers className="text-2xl text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-700">
              Posts from Following
            </h3>
          </div>
          <p className="text-gray-500 mb-4 font-roboto-flex">
            No posts from people you're following yet.
          </p>
        </div>
      )}
    </ContentWithCards>
  );
};

export default Following;
